import {
  ASSORTED_API_BASE_URL,
  SEARCH_API_BASE_URL,
  SOMETHING_WENT_WRONG,
  CONSUMER_API_BASE_URL,
  CONSUMER_WEB_PLATFORM,
  CMS_API_BASE_URL,
} from '@/utils/constants'
import axios, { AxiosRequestConfig } from 'axios'
import { loadState } from '@/utils/localStorage'
import { forceCleanLogout } from '@/utils/sessionCleanup'
import { touchTokenCookie } from '@/helper/touchSession'
import forge from 'node-forge'
import { decryptAES, encryptAES } from '@/utils/forgeencryption'

// Shared secret identifying server-side traffic to Cloud Armor, so the SSR's own
// API calls can bypass the per-IP throttle on connect.agilus.in (priority 1000 of
// the agilus-prod-ing-ca policy). All SSR requests leave Cloud Run from a shared
// Google egress pool, so an IP-keyed rate limit cannot tell them apart from an
// anonymous client.
//
// Deliberately NOT prefixed NEXT_PUBLIC_, and read behind a `typeof window` guard:
// Next.js only inlines NEXT_PUBLIC_* into client bundles, and the guard keeps the
// reference out of client code entirely. Do NOT add this variable to the
// `inject-secrets` sed step in the cloudbuild files — that step rewrites source
// before bundling, which would embed the value in the browser bundle and turn the
// Cloud Armor allow rule into a public bypass.
const SSR_SHARED_SECRET =
  typeof window === 'undefined'
    ? process.env.CONSUMER_WEB_SSR_SECRET
    : undefined

export const ssrSecretHeader = (): Record<string, string> =>
  SSR_SHARED_SECRET ? { 'X-Consumer-Web-SSR': SSR_SHARED_SECRET } : {}

const getTokenFromCookie = (): string | null => {
  if (typeof window !== 'undefined') {
    const cookies = document.cookie.split('; ')
    const tokenCookie = cookies.find((cookie) => cookie.startsWith('token='))

    if (tokenCookie) {
      return tokenCookie.split('=')[1]
    }
  }
  return null
}

export const generateAESKey = () => {
  const aesKey = forge.random.getBytesSync(32)
  return forge.util.encode64(aesKey)
}

const Request = async <T>(
  url: string,
  method: 'get' | 'post' | 'put' | 'delete' = 'get',
  data: any = null,
  headersConfig?: Record<string, string>,
  signal?: AbortSignal
): Promise<T | { status: boolean; data: {} }> => {
  const token = loadState('uidx') || null
  const abcAxis = loadState('abcAxis') || ''
  let payload = data
  const IS_URL_ENCRYPTED = url.includes('/enc/')
  let aesKey = ''
  if (IS_URL_ENCRYPTED) {
    aesKey = generateAESKey()
    const { encryptedData, id } = encryptAES(payload, aesKey)
    payload = { encryptedData, id }
  }

  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: !!token ? `Bearer ${token}` : '',
        App: CONSUMER_WEB_PLATFORM,
        appVersion: '',
        abcAxis,
        ...headersConfig,
        // Spread last so a caller's headersConfig cannot drop the SSR secret.
        ...ssrSecretHeader(),
      },
      method,
      url,
      data: payload,
      signal,
    }

    const response = await axios(config)

    if (token) touchTokenCookie()

    let responseData: any = response.data as T

    if (IS_URL_ENCRYPTED) {
      try {
        responseData = {
          ...responseData,
          data: decryptAES(responseData?.data, aesKey),
        }
      } catch {
        responseData = {
          data: null,
          message: SOMETHING_WENT_WRONG,
          status: false,
        }
      }
    }
    return responseData
  } catch (error: any) {
    if (error?.response?.status === 401) {
      await forceCleanLogout(abcAxis)
    }
    return error?.response?.data || { status: false, data: {} }
  }
}

export const consumerRequest = <T>(
  url: string,
  method: 'get' | 'post' | 'put' | 'delete' = 'get',
  data: any = null,
  headersConfig?: Record<string, string>,
  signal?: AbortSignal
): Promise<T | { status: boolean; data: {} }> => {
  return Request<T>(
    `${CONSUMER_API_BASE_URL}${url}`,
    method,
    data,
    headersConfig,
    signal
  )
}

export const assortedRequest = <T>(
  url: string,
  method: 'get' | 'post' | 'put' | 'delete' = 'get',
  data: any = null,
  headersConfig?: Record<string, string>
): Promise<T | { status: boolean; data: {} }> => {
  return Request<T>(
    `${ASSORTED_API_BASE_URL}${url}`,
    method,
    data,
    headersConfig
  )
}

export const cmsRequest = <T>(
  url: string,
  method: 'get' | 'post' | 'put' | 'delete' = 'get',
  data: any = null,
  headersConfig?: Record<string, string>,
  signal?: AbortSignal
): Promise<T | { status: boolean; data: {} }> => {
  return Request<T>(
    `${CMS_API_BASE_URL}${url}`,
    method,
    data,
    headersConfig,
    signal
  )
}

export const searchRequest = <T>(
  url: string,
  method: 'get' | 'post' | 'put' | 'delete' = 'get',
  data: any = null,
  headersConfig?: Record<string, string>
): Promise<T | { status: boolean; data: {} }> => {
  return Request<T>(`${SEARCH_API_BASE_URL}${url}`, method, data, headersConfig)
}

// Retries a build-time request that would otherwise fail silently to {status:false} and produce zero static pages.
export async function requestWithRetry<T>(
  fn: () => Promise<T | { status: boolean; data: {} }>,
  attempts = 3,
  delayMs = 1000
): Promise<T | { status: boolean; data: {} }> {
  let result = await fn()
  for (let i = 1; i < attempts && (result as any)?.status === false; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs * i))
    result = await fn()
  }
  return result
}
